"use client";

import React, { useState, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Textarea } from "@/app/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/components/ui/select";
import { Alert, AlertDescription } from "@/app/components/ui/alert";
import { Badge } from "@/app/components/ui/badge";
import { Progress } from "@/app/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import { Switch } from "@/app/components/ui/switch";
import { 
  Mic, 
  Upload, 
  Download, 
  Copy, 
  Loader2, 
  AlertTriangle, 
  CheckCircle,
  FileAudio,
  Zap,
  Clock,
  DollarSign,
  Volume2,
  Settings,
  Info,
  Play,
  Pause,
  StopCircle
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";

const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'it', name: 'Italian' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'ru', name: 'Russian' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'zh', name: 'Chinese' },
];

export default function SpeechToTextPage() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [transcriptionResult, setTranscriptionResult] = useState("");
  const [confidence, setConfidence] = useState<number | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [enhanceAudio, setEnhanceAudio] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeTab, setActiveTab] = useState("upload");
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement>(null);

  // TRPC queries and mutations
  const { data: usageData, refetch: refetchUsage } = trpc.speechToText.getUsage.useQuery();
  const { data: settingsData } = trpc.speechToText.getSettings.useQuery();
  const transcribeMutation = trpc.speechToText.transcribe.useMutation();

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        setAudioBlob(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.success("Recording started!");
    } catch (error) {
      toast.error("Failed to start recording. Please check microphone permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      toast.success("Recording stopped!");
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const supportedTypes = settingsData?.supportedFormats || ['mp3', 'wav', 'mp4', 'webm', 'm4a'];
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      
      if (!fileExtension || !supportedTypes.includes(fileExtension)) {
        toast.error(`Unsupported file format. Supported formats: ${supportedTypes.join(', ')}`);
        return;
      }

      // Validate file size
      const maxSize = settingsData?.maxFileSize || 25 * 1024 * 1024; // 25MB default
      if (file.size > maxSize) {
        toast.error(`File too large. Maximum size: ${formatBytes(maxSize)}`);
        return;
      }

      setSelectedFile(file);
      setAudioBlob(null);
      toast.success("File selected successfully!");
    }
  };

  const processAudio = async () => {
    const audioToProcess = audioBlob || selectedFile;
    if (!audioToProcess) {
      toast.error("Please select an audio file or record audio first.");
      return;
    }

    setIsProcessing(true);
    setTranscriptionResult("");
    setConfidence(null);

    try {
      // Convert to base64
      const arrayBuffer = await audioToProcess.arrayBuffer();
      const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));

      const result = await transcribeMutation.mutateAsync({
        audioData: base64,
        format: selectedFile?.name.split('.').pop() || 'wav',
        language: selectedLanguage,
        enhanceAudio,
      });

      setTranscriptionResult(result.text);
      setConfidence(result.confidence);
      
      // Refresh usage data
      await refetchUsage();

      toast.success(`Transcription completed! ${result.wordsTranscribed} words transcribed.`);

    } catch (error: any) {
      console.error('Transcription error:', error);
      toast.error(error.message || "Failed to transcribe audio");
    } finally {
      setIsProcessing(false);
    }
  };

  const playAudio = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const copyToClipboard = () => {
    if (transcriptionResult) {
      navigator.clipboard.writeText(transcriptionResult);
      toast.success("Transcription copied to clipboard!");
    }
  };

  const downloadTranscription = () => {
    if (transcriptionResult) {
      const blob = new Blob([transcriptionResult], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'transcription.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Transcription downloaded!");
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getUsagePercentage = (used: number, limit: number) => {
    if (limit === -1) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4">Speech to Text</h1>
          <p className="text-muted-foreground text-lg">
            Convert audio recordings and files to accurate text transcriptions
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card className="h-fit">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Settings & Usage
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Usage Information */}
                {usageData && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-blue-500" />
                      <span className="text-sm font-medium">Usage ({usageData.tier})</span>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <span>Daily Requests</span>
                          <span className="font-mono">
                            {usageData.usage.daily.requests}
                            {usageData.limits?.daily?.requests && usageData.limits.daily.requests !== -1 
                              ? `/${usageData.limits.daily.requests}` 
                              : '/∞'
                            }
                          </span>
                        </div>
                        {usageData.limits?.daily?.requests && usageData.limits.daily.requests !== -1 && (
                          <Progress 
                            value={getUsagePercentage(usageData.usage.daily.requests, usageData.limits.daily.requests)} 
                            className="h-2" 
                          />
                        )}
                      </div>
                      
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <span>Data Processed</span>
                          <span className="font-mono">
                            {formatBytes(usageData.usage.daily.dataSizeBytes)}
                            {usageData.limits?.daily?.dataSize && usageData.limits.daily.dataSize !== -1 
                              ? `/${formatBytes(usageData.limits.daily.dataSize)}` 
                              : '/∞'
                            }
                          </span>
                        </div>
                        {usageData.limits?.daily?.dataSize && usageData.limits.daily.dataSize !== -1 && (
                          <Progress 
                            value={getUsagePercentage(usageData.usage.daily.dataSizeBytes, usageData.limits.daily.dataSize)} 
                            className="h-2" 
                          />
                        )}
                      </div>
                      
                      <div className="flex justify-between text-xs">
                        <span>Cost Today</span>
                        <span className="font-mono">${usageData.usage.daily.costUsd.toFixed(4)}</span>
                      </div>

                      {usageData.usage.daily.customMetrics?.wordsTranscribed && (
                        <div className="flex justify-between text-xs">
                          <span>Words Transcribed</span>
                          <span className="font-mono">{usageData.usage.daily.customMetrics.wordsTranscribed}</span>
                        </div>
                      )}
                    </div>

                    {usageData.tier === 'free' && usageData.usage.daily.requests >= (usageData.limits?.daily?.requests || 0) * 0.8 && (
                      <Alert className="border-orange-200 bg-orange-50">
                        <AlertTriangle className="h-4 w-4 text-orange-600" />
                        <AlertDescription className="text-xs text-orange-800">
                          You're approaching your daily limit. 
                          <a href="/pricing" className="font-medium underline ml-1">Upgrade to Pro</a> for higher limits.
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                )}

                {/* Language Selection */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Language</label>
                  <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SUPPORTED_LANGUAGES.map((lang) => (
                        <SelectItem key={lang.code} value={lang.code}>
                          {lang.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Audio Enhancement */}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <label className="text-sm font-medium">Enhance Audio</label>
                    <p className="text-xs text-muted-foreground">Improve quality for better transcription</p>
                  </div>
                  <Switch checked={enhanceAudio} onCheckedChange={setEnhanceAudio} />
                </div>

                {/* Settings Info */}
                {settingsData && (
                  <div className="p-3 bg-muted/50 rounded-lg space-y-2">
                    <div className="flex items-center gap-2">
                      <Info className="h-4 w-4 text-blue-500" />
                      <span className="text-sm font-medium">Limits</span>
                    </div>
                    <div className="text-xs space-y-1">
                      <div>Max file size: {formatBytes(settingsData.maxFileSize)}</div>
                      <div>Formats: {settingsData.supportedFormats.join(', ')}</div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mic className="h-5 w-5" />
                  Audio Input
                </CardTitle>
                <CardDescription>
                  Upload an audio file or record directly from your microphone
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="upload">Upload File</TabsTrigger>
                    <TabsTrigger value="record">Record Audio</TabsTrigger>
                  </TabsList>

                  <TabsContent value="upload" className="space-y-4">
                    <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8">
                      <div className="text-center space-y-4">
                        <FileAudio className="h-12 w-12 text-muted-foreground mx-auto" />
                        <div>
                          <p className="text-sm font-medium">Choose an audio file</p>
                          <p className="text-xs text-muted-foreground">
                            Supports: {settingsData?.supportedFormats.join(', ') || 'MP3, WAV, MP4, WEBM, M4A'}
                          </p>
                        </div>
                        <Button onClick={() => fileInputRef.current?.click()}>
                          <Upload className="h-4 w-4 mr-2" />
                          Select File
                        </Button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="audio/*"
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                      </div>
                    </div>

                    {selectedFile && (
                      <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                        <FileAudio className="h-8 w-8 text-blue-500" />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{selectedFile.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatBytes(selectedFile.size)}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedFile(null)}
                        >
                          Remove
                        </Button>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="record" className="space-y-4">
                    <div className="text-center space-y-4 p-8">
                      <div className="flex justify-center">
                        {isRecording ? (
                          <div className="relative">
                            <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
                              <Mic className="h-8 w-8 text-white" />
                            </div>
                            <div className="absolute -inset-2 border-2 border-red-500 rounded-full animate-ping" />
                          </div>
                        ) : (
                          <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center">
                            <Mic className="h-8 w-8 text-white" />
                          </div>
                        )}
                      </div>

                      <div>
                        <p className="text-sm font-medium">
                          {isRecording ? 'Recording...' : 'Ready to record'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Click the button below to {isRecording ? 'stop' : 'start'} recording
                        </p>
                      </div>

                      <Button
                        onClick={isRecording ? stopRecording : startRecording}
                        variant={isRecording ? "destructive" : "default"}
                        size="lg"
                      >
                        {isRecording ? (
                          <>
                            <StopCircle className="h-4 w-4 mr-2" />
                            Stop Recording
                          </>
                        ) : (
                          <>
                            <Mic className="h-4 w-4 mr-2" />
                            Start Recording
                          </>
                        )}
                      </Button>
                    </div>

                    {audioBlob && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                          <Volume2 className="h-8 w-8 text-green-500" />
                          <div className="flex-1">
                            <p className="text-sm font-medium">Recording complete</p>
                            <p className="text-xs text-muted-foreground">
                              {formatBytes(audioBlob.size)}
                            </p>
                          </div>
                          <Button variant="outline" size="sm" onClick={playAudio}>
                            {isPlaying ? (
                              <>
                                <Pause className="h-4 w-4 mr-2" />
                                Pause
                              </>
                            ) : (
                              <>
                                <Play className="h-4 w-4 mr-2" />
                                Play
                              </>
                            )}
                          </Button>
                        </div>
                        
                        <audio
                          ref={audioRef}
                          src={URL.createObjectURL(audioBlob)}
                          onEnded={() => setIsPlaying(false)}
                          className="hidden"
                        />
                      </div>
                    )}
                  </TabsContent>

                  {/* Process Button */}
                  <div className="flex justify-center">
                    <Button
                      onClick={processAudio}
                      disabled={isProcessing || (!selectedFile && !audioBlob)}
                      size="lg"
                      className="min-w-48"
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Transcribing...
                        </>
                      ) : (
                        <>
                          <Mic className="h-4 w-4 mr-2" />
                          Transcribe Audio
                        </>
                      )}
                    </Button>
                  </div>
                </Tabs>
              </CardContent>
            </Card>

            {/* Results */}
            {(transcriptionResult || isProcessing) && (
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    Transcription Result
                  </CardTitle>
                  {confidence && (
                    <CardDescription>
                      Confidence: {(confidence * 100).toFixed(1)}%
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  <Textarea
                    value={transcriptionResult}
                    onChange={(e) => setTranscriptionResult(e.target.value)}
                    placeholder={isProcessing ? "Transcribing your audio..." : "Transcription will appear here..."}
                    className="min-h-32"
                    readOnly={isProcessing}
                  />

                  {transcriptionResult && (
                    <div className="flex gap-2">
                      <Button onClick={copyToClipboard} variant="outline">
                        <Copy className="h-4 w-4 mr-2" />
                        Copy
                      </Button>
                      <Button onClick={downloadTranscription} variant="outline">
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}